import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { adminApi, AdminUserSummary } from '@/lib/admin';
import { AdminShell } from '@/components/app/AdminShell';
import { ConfirmDialog, InlineAlert, Modal } from '@/components/app/Modal';

export function AdminUsersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [adminTarget, setAdminTarget] = useState<{ user: AdminUserSummary; promote: boolean } | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<AdminUserSummary | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', search],
    queryFn: () => adminApi.listUsers({ search: search || undefined, limit: 100 }),
  });

  const setAdmin = useMutation({
    mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) =>
      adminApi.setAdmin(userId, isAdmin),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      setAdminTarget(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const setSuspend = useMutation({
    mutationFn: ({ userId, suspended, reason }: { userId: string; suspended: boolean; reason: string | null }) =>
      adminApi.setSuspended(userId, suspended, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      setSuspendTarget(null);
      setSuspendReason('');
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <AdminShell
      title={t('admin.users.title', { defaultValue: 'Users' })}
      description={t('admin.users.subtitle', { defaultValue: 'All accounts across every tenant. Promote, demote, suspend, reinstate.' }) as string}
      actions={
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('admin.users.search_placeholder', { defaultValue: 'Search email or name…' }) as string}
          className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error" onDismiss={() => setError(null)}>{error}</InlineAlert>}

        {isLoading || !data ? (
          <div className="card-paper p-10 text-center text-ink-500 text-sm">{t('common.loading')}</div>
        ) : data.rows.length === 0 ? (
          <div className="card-paper p-12 text-center text-ink-500 text-sm">
            {t('admin.users.empty', { defaultValue: 'No users match this filter.' })}
          </div>
        ) : (
          <div className="card-paper overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-500 border-b border-ink-100 bg-paper-100 text-[11px] uppercase tracking-[0.12em]">
                  <th className="py-2.5 pl-5 pr-3 font-medium">{t('admin.users.th_user')}</th>
                  <th className="py-2.5 px-3 font-medium">{t('admin.users.th_status')}</th>
                  <th className="py-2.5 px-3 font-medium">{t('admin.users.th_signin')}</th>
                  <th className="py-2.5 px-3 font-medium">{t('admin.users.th_created')}</th>
                  <th className="py-2.5 px-3 pr-5 font-medium text-right">{t('admin.users.th_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((u) => (
                  <tr key={u.id} className="border-b border-ink-50 align-top hover:bg-paper-100/40">
                    <td className="py-3 pl-5 pr-3">
                      <div className="text-ink-900 font-medium">{u.name || '—'}</div>
                      <div className="text-[12px] text-ink-500 num">{u.email}</div>
                      <div className="text-[10.5px] text-ink-400 num mt-0.5">{u.id.slice(0, 8)}{u.countryCode ? ` · ${u.countryCode}` : ''}</div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex flex-wrap gap-1.5">
                        {u.isSiteAdmin && <span className="chip-citation text-[10.5px]">{t('admin.users.admin_chip', { defaultValue: 'admin' })}</span>}
                        {!u.emailVerified && (
                          <span className="rounded-md bg-warn-500/10 border border-warn-500/30 text-warn-600 text-[10.5px] px-1.5 py-0.5">
                            {t('admin.users.unverified', { defaultValue: 'unverified' })}
                          </span>
                        )}
                        {u.suspendedAt && (
                          <span className="rounded-md bg-warn-500 text-white text-[10.5px] px-1.5 py-0.5">
                            {t('admin.users.suspended_chip', { defaultValue: 'suspended' })}
                          </span>
                        )}
                        {!u.isSiteAdmin && u.emailVerified && !u.suspendedAt && (
                          <span className="rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10.5px] px-1.5 py-0.5">
                            {t('admin.users.active', { defaultValue: 'active' })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-[12px] text-ink-600">
                      <div className="flex flex-wrap gap-1">
                        {u.hasPassword && <span className="num text-ink-700">pwd</span>}
                        {u.oauthLinked.map((p) => <span key={p} className="num text-accent-600">{p}</span>)}
                      </div>
                    </td>
                    <td className="py-3 px-3 num text-[12px] text-ink-600 whitespace-nowrap">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 pl-3 pr-5 text-right">
                      <div className="inline-flex gap-1.5">
                        <button
                          onClick={() => setAdminTarget({ user: u, promote: !u.isSiteAdmin })}
                          className="btn-ghost px-2.5 py-1 text-[12px]"
                        >
                          {u.isSiteAdmin
                            ? t('admin.users.demote', { defaultValue: 'Demote' })
                            : t('admin.users.promote', { defaultValue: 'Promote' })}
                        </button>
                        <button
                          onClick={() => { setSuspendTarget(u); setSuspendReason(u.suspendedReason ?? ''); }}
                          className="px-2.5 py-1 text-[12px] rounded-lg text-warn-600 border border-warn-500/30 hover:bg-warn-500/10 transition font-medium"
                        >
                          {u.suspendedAt
                            ? t('admin.users.reinstate', { defaultValue: 'Reinstate' })
                            : t('admin.users.suspend', { defaultValue: 'Suspend' })}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 text-[11px] text-ink-500 border-t border-ink-100">
              {t('admin.users.showing', {
                defaultValue: 'Showing {{shown}} of {{total}}',
                shown: data.rows.length,
                total: data.total,
              })}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!adminTarget}
        onOpenChange={(next) => { if (!next) setAdminTarget(null); }}
        title={
          adminTarget?.promote
            ? t('admin.users.promote_title', { defaultValue: 'Grant site admin?' })
            : t('admin.users.demote_title', { defaultValue: 'Revoke site admin?' })
        }
        description={
          adminTarget
            ? (t('admin.users.promote_body', {
                defaultValue: 'This grants {{name}} full cross-tenant administrative access.',
                name: adminTarget.user.email || adminTarget.user.name || adminTarget.user.id.slice(0, 8),
              }) as string)
            : undefined
        }
        confirmLabel={
          adminTarget?.promote
            ? (t('admin.users.confirm_promote', { defaultValue: 'Grant access' }) as string)
            : (t('admin.users.confirm_demote', { defaultValue: 'Revoke access' }) as string)
        }
        destructive={!adminTarget?.promote}
        onConfirm={() => {
          if (adminTarget) setAdmin.mutate({ userId: adminTarget.user.id, isAdmin: adminTarget.promote });
        }}
        pending={setAdmin.isPending}
      />

      <Modal
        open={!!suspendTarget}
        onOpenChange={(next) => { if (!next) { setSuspendTarget(null); setSuspendReason(''); } }}
        title={
          suspendTarget?.suspendedAt
            ? t('admin.users.reinstate_title', { defaultValue: 'Reinstate this user?' })
            : t('admin.users.suspend_title', { defaultValue: 'Suspend this user?' })
        }
        description={
          suspendTarget
            ? (t('admin.users.suspend_body', {
                defaultValue: '{{email}} will be {{verb}} immediately. They can be re-toggled at any time.',
                email: suspendTarget.email || suspendTarget.name || suspendTarget.id.slice(0, 8),
                verb: suspendTarget.suspendedAt
                  ? t('admin.users.verb_reinstated', { defaultValue: 'reinstated' })
                  : t('admin.users.verb_suspended', { defaultValue: 'suspended' }),
              }) as string)
            : undefined
        }
      >
        {suspendTarget && !suspendTarget.suspendedAt && (
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.12em] text-ink-500 mb-1 font-semibold">
              {t('admin.users.suspend_reason', { defaultValue: 'Reason (visible in audit trail)' })}
            </span>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            />
          </label>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setSuspendTarget(null)}
            className="btn-ghost px-3.5 py-2 text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!suspendTarget) return;
              setSuspend.mutate({
                userId: suspendTarget.id,
                suspended: !suspendTarget.suspendedAt,
                reason: suspendTarget.suspendedAt ? null : (suspendReason || null),
              });
            }}
            disabled={setSuspend.isPending}
            className={
              suspendTarget?.suspendedAt
                ? 'btn-press px-3.5 py-2 text-sm'
                : 'inline-flex items-center px-3.5 py-2 rounded-lg text-sm font-semibold bg-warn-500 text-white hover:bg-warn-600 shadow-press disabled:opacity-60'
            }
          >
            {setSuspend.isPending
              ? '…'
              : suspendTarget?.suspendedAt
                ? t('admin.users.confirm_reinstate', { defaultValue: 'Reinstate' })
                : t('admin.users.confirm_suspend', { defaultValue: 'Suspend' })}
          </button>
        </div>
      </Modal>
    </AdminShell>
  );
}
